import _ from 'lodash'
import moment from 'moment'
import { useCallback, useMemo } from 'react'

import { useGroupFilter } from '../../../groupFilter'
import useLocationMetricTrendsQuery, {
  IGroupByType,
  IOptionsType as ITrendsOptionsType,
} from '../../../hooks/useLocationMetricTrendsQuery'
import useLocationMetricValuesQuery, {
  IOptionsType as IMetricOptionsType,
} from '../../../hooks/useLocationMetricValuesQuery'
import calc from '../../../utils/calc'
import { corporateGroupTableConfigs, useVariables } from '../../../variables'
import { IApiDataType } from '../../types'

export const pAndLRankingsConfigs = {
  '<%- JSON(corporateGroup?.tableColumns.slice(0, 1)) %>':
    corporateGroupTableConfigs['<%- JSON(corporateGroup?.tableColumns) %>'],
  '<%- JSON(pnlRankings.columns) %>': 'price',
  rowIndex: 'string',
  pAndLSales: 'price',
  pAndLSalesPercent: 'percent',
  pAndLYoySales: 'price',
  pAndLYoySalesPercent: 'percent',
  pAndLYoyPercent: 'percent',
  pAndLYtdSales: 'price',
  pAndLYtdSalesPercent: 'percent',
  pAndLLyYtdSales: 'price',
  pAndLLyYtdSalesPercent: 'percent',
  pAndLLyYtdPercent: 'percent',
  pAndL1PastSales: 'price',
  pAndL1PastSalesPercent: 'percent',
  pAndL2PastSales: 'price',
  pAndL2PastSalesPercent: 'percent',
  pAndLSalesTrand: 'percent',
} as const

const usePAndLRankings = () => {
  const { variables } = useVariables()
  const { groupFilter } = useGroupFilter()
  const isLegalEntity = groupFilter?.id.indexOf('group') === 0
  const { loading: metricLoading, ...allMetricData } =
    useLocationMetricValuesQuery({
      metricGroups: ((): IMetricOptionsType['metricGroups'] => {
        switch (variables.pnlRankings?.rankBy.value[0]?.[0]) {
          case 'pAndLCurrentCompare':
            return ['PnL']
          case 'pAndLYoyCompare':
            return ['PnL', { key: 'PnL', type: 'yoy' }]
          case 'pAndLLYYtdCompare':
            return [
              { key: 'PnL', type: 'ytd' },
              { key: 'PnL', type: 'prior ytd' },
            ]
          default:
            return []
        }
      })(),
      fields: ['metricData'],
      handler: (variables) => {
        const newVariables = {
          ...variables,
          iFilter: {
            ...variables.iFilter,
            ...(isLegalEntity
              ? {
                  location_group_ids: groupFilter?.ids,
                }
              : {
                  location_ids: groupFilter?.ids,
                }),
          },
        }

        return {
          variables: newVariables,
          skip:
            !groupFilter ||
            !newVariables.iStartDate ||
            !newVariables.iEndDate ||
            (newVariables.iFilter.metric_groups || []).length === 0,
        }
      },
    })
  const { loading: trendsLoading, ...allTrendsData } =
    useLocationMetricTrendsQuery({
      metricGroups:
        variables.pnlRankings?.rankBy.value[0]?.[0] !== 'pAndLTrailing3Period'
          ? []
          : ['PnL'],
      dateRange: `last_3_${
        variables.date?.getInfo(0)?.displayType.toLowerCase() || 'week'
      }s` as IGroupByType,
      fields: ['metricData'],
      handler: (variables) => {
        const newVariables = {
          ...variables,
          iFilter: {
            ...variables.iFilter,
            ...(isLegalEntity
              ? {
                  location_group_ids: groupFilter?.ids,
                }
              : {
                  location_ids: groupFilter?.ids,
                }),
          },
        }

        return {
          variables: newVariables,
          skip:
            !groupFilter ||
            !newVariables.iStartDate ||
            !newVariables.iEndDate ||
            (newVariables.iFilter.metric_groups || []).length === 0,
        }
      },
    })

  const metricMapping = useMemo(() => {
    const data =
      (allMetricData.data ||
        allMetricData.yoyData ||
        allMetricData.ytdData ||
        allMetricData.priorYtdData ||
        allTrendsData.data)?.[0].metricData || {}

    return Object.keys(data).reduce(
      (result, key) => ({
        ...result,
        [data[key].name]: key,
      }),
      {} as Record<string, string>,
    )
  }, [allMetricData, allTrendsData])
  const dateIndexMapping = useMemo(() => {
    if (!allTrendsData.data || !allTrendsData.data[0]) return {}

    const firstItem = allTrendsData.data[0]
    const firstId =
      'locationId' in firstItem
        ? firstItem.locationId
        : firstItem.locationGroupId
    const items = allTrendsData.data
      .filter(
        (d) =>
          ('locationId' in d ? d.locationId : d.locationGroupId) === firstId,
      )
      .sort((a, b) =>
        moment.utc(a.groupByStartDate).isAfter(b.groupByStartDate) ? -1 : 1,
      )

    return items.reduce(
      (result, item, index) => ({
        ...result,
        [item.groupByStartDate]: index,
      }),
      {} as Record<string, number>,
    )
  }, [allTrendsData])

  const getValues = useCallback(
    (
      dataKey: 'data' | 'yoyData' | 'ytdData' | 'priorYtdData',
      metricKey: 'metrics' | 'salesMetrics' = 'metrics',
    ) => {
      const data = allMetricData[dataKey] || allTrendsData[dataKey as 'data']

      return data?.reduce(
        (result, d): Record<string, ReturnType<typeof calc>> => {
          const id = [
            'locationId' in d ? d.locationId : d.locationGroupId,
            'groupByStartDate' in d
              ? dateIndexMapping[
                  (d as NonNullable<typeof allTrendsData.data>[number])
                    .groupByStartDate
                ]
              : 0,
          ].join('-')
          const value = (variables.pnlRankings?.[metricKey] || []).reduce(
            (result, metricSetting) => {
              const { key: metric, operator } =
                typeof metricSetting === 'string'
                  ? { key: metricSetting, operator: '+' as const }
                  : metricSetting
              const key = metricMapping[metric]

              if (!(key in d.metricData)) return result

              return _.isNil(result) && _.isNil(d.metricData[key]?.value)
                ? null
                : calc(result || 0, operator, d.metricData[key]?.value || 0)
            },
            null as ReturnType<typeof calc>,
          )

          return {
            ...result,
            [id]: value,
          }
        },
        {} as Record<string, ReturnType<typeof calc>>,
      )
    },
    [variables, allMetricData, allTrendsData, metricMapping, dateIndexMapping],
  )

  return {
    data: useMemo((): IApiDataType => {
      if (!variables.corporateGroup) return null

      const pAndLSalesById = getValues('data')
      const totalSalesById = getValues('data', 'salesMetrics')
      const pAndLYoySalesById = getValues('yoyData')
      const totalYoySalesById = getValues('yoyData', 'salesMetrics')
      const pAndLYtdSalesById = getValues('ytdData')
      const totalYtdSalesById = getValues('ytdData', 'salesMetrics')
      const pAndLLyYtdSalesById = getValues('priorYtdData')
      const totalLyYtdSalesById = getValues('priorYtdData', 'salesMetrics')
      const groupInfo =
        'locations' in variables.corporateGroup
          ? variables.corporateGroup.locations
          : variables.corporateGroup.locationGroups
      const sorting = variables.pnlRankings?.sorting

      const data = Object.values(groupInfo).map((l) => {
        const id = `${l.id}-0`
        const pAndLSales = pAndLSalesById?.[id]
        const totalSales = totalSalesById?.[id]
        const pAndLYoySales = pAndLYoySalesById?.[id]
        const totalYoySales = totalYoySalesById?.[id]
        const pAndLYtdSales = pAndLYtdSalesById?.[id]
        const totalYtdSales = totalYtdSalesById?.[id]
        const pAndLLyYtdSales = pAndLLyYtdSalesById?.[id]
        const totalLyYtdSales = totalLyYtdSalesById?.[id]
        const pAndL1PastSales = pAndLSalesById?.[`${l.id}-1`]
        const totalPAndL1PastSales = totalSalesById?.[`${l.id}-1`]
        const pAndL2PastSales = pAndLSalesById?.[`${l.id}-2`]
        const totalPAndL2PastSales = totalSalesById?.[`${l.id}-2`]

        return {
          ...groupInfo[l.id].tableRow,
          id: l.id.toString(),
          parentId: 'root',
          pAndLSales,
          pAndLSalesPercent: calc(pAndLSales, 'percentageOf', totalSales),
          pAndLYoySales,
          pAndLYoySalesPercent: calc(
            pAndLYoySales,
            'percentageOf',
            totalYoySales,
          ),
          pAndLYoyPercent: calc(
            pAndLSales,
            'variancePercentageOf',
            pAndLYoySales,
          ),
          pAndLYtdSales,
          pAndLYtdSalesPercent: calc(
            pAndLYtdSales,
            'percentageOf',
            totalYtdSales,
          ),
          pAndLLyYtdSales,
          pAndLLyYtdSalesPercent: calc(
            pAndLLyYtdSales,
            'percentageOf',
            totalLyYtdSales,
          ),
          pAndLLyYtdPercent: calc(
            pAndLYtdSales,
            'variancePercentageOf',
            pAndLLyYtdSales,
          ),
          pAndL1PastSales,
          pAndL1PastSalesPercent: calc(
            pAndL1PastSales,
            'percentageOf',
            totalPAndL1PastSales,
          ),
          pAndL2PastSales,
          pAndL2PastSalesPercent: calc(
            pAndL2PastSales,
            'percentageOf',
            totalPAndL2PastSales,
          ),
          pAndLSalesTrand: calc(
            pAndLSales,
            'variancePercentageOf',
            pAndL2PastSales,
          ),
        }
      })

      return !sorting
        ? data
        : data.sort((a, b) => {
            const aValue = a[sorting.key as keyof typeof a] ?? -Infinity
            const bValue = b[sorting.key as keyof typeof b] ?? -Infinity
            if (aValue === -Infinity) {
              return 1
            }

            if (bValue === -Infinity) {
              return -1
            }

            return (aValue - bValue) * (sorting.desc ? -1 : 1)
          })
    }, [variables, allMetricData, allTrendsData, getValues]),
    loading: metricLoading || trendsLoading,
  }
}

export default usePAndLRankings
